import React, { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { Clock, TrendingUp, AlertTriangle, CheckCircle, Calendar, Timer, Activity } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { getHoursBankStatus, getHoursBank } from '../../services/api';
import { TaskAreaLabels } from '../../types/task';
import { HoursBankAreaData } from '../../types/hoursBank';

interface HoursUtilizationDashboardDarkProps {
  projectId: string;
  billingMonth: number;
  billingYear: number;
  className?: string;
}

interface AreaHoursData {
  area: string;
  allocatedHours: number;
  usedHours: number;
  availableHours: number;
  utilizationPercentage: number;
  status: 'low' | 'medium' | 'high' | 'critical';
}

export const HoursUtilizationDashboardDark: React.FC<HoursUtilizationDashboardDarkProps> = ({
  projectId,
  billingMonth,
  billingYear,
  className = ''
}) => {
  const [loading, setLoading] = useState(true);
  const [hoursTrackingEnabled, setHoursTrackingEnabled] = useState(false);
  const [areasData, setAreasData] = useState<AreaHoursData[]>([]);
  const [totals, setTotals] = useState({
    allocatedHours: 0,
    usedHours: 0,
    availableHours: 0,
    utilizationPercentage: 0
  });

  useEffect(() => {
    loadHoursData();
  }, [projectId, billingMonth, billingYear]);

  const loadHoursData = async () => {
    try {
      setLoading(true);

      // Check if hours tracking is enabled
      const statusResponse = await getHoursBankStatus(projectId);
      const enabled = statusResponse.data.data.hoursTrackingEnabled;
      setHoursTrackingEnabled(enabled);

      if (!enabled) {
        setLoading(false);
        return;
      }

      // Use billing period from service order, not start date
      const month = billingMonth;
      const year = billingYear;

      // Load hours bank data
      const hoursResponse = await getHoursBank(projectId, year, month);
      if (hoursResponse.data.data.enabled && hoursResponse.data.data.hoursBank) {
        const bankData = hoursResponse.data.data.hoursBank;
        const { totals: responseTotals } = hoursResponse.data.data;

        // Process areas data
        const areas: AreaHoursData[] = [];
        Object.entries(bankData).forEach(([area, data]) => {
          if (data && typeof data === 'object' && 'allocatedHours' in data) {
            const areaData = data as HoursBankAreaData;
            const utilizationPercentage = areaData.allocatedHours > 0 
              ? Math.round((areaData.usedHours / areaData.allocatedHours) * 100)
              : 0;

            let status: AreaHoursData['status'] = 'low';
            if (utilizationPercentage >= 90) status = 'critical';
            else if (utilizationPercentage >= 75) status = 'high';
            else if (utilizationPercentage >= 50) status = 'medium';

            areas.push({
              area,
              allocatedHours: areaData.allocatedHours,
              usedHours: areaData.usedHours,
              availableHours: areaData.availableHours,
              utilizationPercentage,
              status
            });
          }
        });

        setAreasData(areas);
        setTotals({
          allocatedHours: responseTotals.allocatedHours,
          usedHours: responseTotals.usedHours,
          availableHours: responseTotals.availableHours,
          utilizationPercentage: responseTotals.allocatedHours > 0 
            ? Math.round((responseTotals.usedHours / responseTotals.allocatedHours) * 100)
            : 0
        });
      }
    } catch (error) {
      console.error('Error loading hours data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: AreaHoursData['status']) => {
    switch (status) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-orange-400" />;
      case 'medium':
        return <TrendingUp className="w-4 h-4 text-yellow-400" />;
      default:
        return <CheckCircle className="w-4 h-4 text-green-400" />;
    }
  };

  if (!hoursTrackingEnabled) {
    return null; // Don't show anything if hours tracking is disabled
  }

  if (loading) {
    return (
      <Card variant="glass" className={className}>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-foreground-primary">Banco de Horas Mensal</h3>
          </div>
          <div className="text-center text-foreground-tertiary">
            Carregando dados do banco de horas...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (areasData.length === 0) {
    return (
      <Card variant="glass" className={className}>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-foreground-primary">Banco de Horas Mensal</h3>
          </div>
          <div className="text-center text-foreground-tertiary">
            Banco de horas não configurado para este período
          </div>
        </CardContent>
      </Card>
    );
  }

  // Capped utilization for progress bar (visual limit at 100%)
  const cappedUtilization = Math.min(100, totals.utilizationPercentage || 0);

  return (
    <Card variant="glass" className={className}>
      <CardContent className="p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-foreground-primary">Banco de Horas Mensal</h3>
        </div>

        {/* Resumo Total de Horas */}
        <Card variant="glass" className="p-4 border-blue-500/20 bg-gradient-to-r from-blue-500/5 to-cyan-500/5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Total Contratado */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20 border border-blue-500/30">
                  <Calendar className="w-5 h-5 text-blue-400" aria-hidden="true" focusable="false" />
                </div>
                <div>
                  <p className="text-xs text-foreground-tertiary">Horas Contratadas</p>
                  <p className="text-lg font-semibold text-foreground-primary">{totals.allocatedHours}h</p>
                </div>
              </div>
            </div>

            {/* Total Utilizado */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/20 border border-orange-500/30">
                  <Timer className="w-5 h-5 text-orange-400" aria-hidden="true" focusable="false" />
                </div>
                <div>
                  <p className="text-xs text-foreground-tertiary">Horas Utilizadas</p>
                  <p className="text-lg font-semibold text-foreground-primary">{totals.usedHours}h</p>
                  <p className="text-xs text-foreground-tertiary">({totals.utilizationPercentage || 0}%)</p>
                </div>
              </div>
            </div>

            {/* Saldo Disponível */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg border ${
                  totals.availableHours < 0 
                    ? 'bg-red-500/20 border-red-500/30' 
                    : 'bg-green-500/20 border-green-500/30'
                }`}>
                  {totals.availableHours < 0 ? (
                    <AlertTriangle className="w-5 h-5 text-red-400" aria-hidden="true" focusable="false" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-400" aria-hidden="true" focusable="false" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-foreground-tertiary">
                    {totals.availableHours < 0 ? 'Horas Excedidas' : 'Saldo Disponível'}
                  </p>
                  <p className={`text-lg font-semibold ${
                    totals.availableHours < 0 ? 'text-red-400' : 'text-green-400'
                  }`}>
                    {Math.abs(totals.availableHours)}h
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Barra de Progresso Visual */}
          <div className="mt-4 space-y-2">
            <div className="flex justify-between items-center text-xs text-foreground-tertiary">
              <span>Utilização Total</span>
              <span>{totals.utilizationPercentage || 0}%</span>
            </div>
            <div 
              className="bg-white/10 rounded-full h-3 overflow-hidden border border-white/20"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={cappedUtilization}
              aria-label={`Utilização total de horas: ${totals.utilizationPercentage || 0}%`}
            >
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  (totals.utilizationPercentage || 0) >= 100 || totals.availableHours < 0
                    ? 'bg-gradient-to-r from-red-500 to-red-600'
                    : (totals.utilizationPercentage || 0) >= 90
                    ? 'bg-gradient-to-r from-red-500 to-orange-500'
                    : (totals.utilizationPercentage || 0) >= 75
                    ? 'bg-gradient-to-r from-orange-500 to-yellow-500'
                    : (totals.utilizationPercentage || 0) >= 50
                    ? 'bg-gradient-to-r from-yellow-500 to-blue-500'
                    : 'bg-gradient-to-r from-green-500 to-blue-500'
                }`}
                style={{ width: `${cappedUtilization}%` }}
              />
            </div>
          </div>
        </Card>

        {/* Areas Breakdown */}
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {areasData.map((areaData) => (
              <Card key={areaData.area} variant="glass" className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {TaskAreaLabels[areaData.area as keyof typeof TaskAreaLabels] || areaData.area}
                    </Badge>
                    {getStatusIcon(areaData.status)}
                  </div>
                  <div className="text-xs text-foreground-tertiary">
                    {areaData.utilizationPercentage}%
                  </div>
                </div>
                
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-foreground-tertiary">
                    <span>{areaData.usedHours}h usado</span>
                    <span className={areaData.availableHours < 0 ? 'text-red-400' : ''}>
                      {areaData.availableHours}h {areaData.availableHours < 0 ? 'excedido' : 'disponível'}
                    </span>
                  </div>
                  
                  {/* Progress Bar - Dark Theme */}
                  <div className="bg-white/10 rounded-full h-2 overflow-hidden border border-white/20">
                    <div 
                      className={`h-full rounded-full transition-all duration-300 ${
                        areaData.status === 'critical' || areaData.availableHours < 0 
                          ? 'bg-gradient-to-r from-red-500 to-red-600' 
                          : areaData.status === 'high' 
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600'
                          : areaData.status === 'medium'
                          ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
                          : 'bg-gradient-to-r from-green-500 to-green-600'
                      }`}
                      style={{ width: `${Math.min(areaData.utilizationPercentage, 100)}%` }}
                    />
                  </div>
                  
                  <div className="text-xs text-foreground-tertiary text-center">
                    {areaData.allocatedHours}h total contratado
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Warnings */}
        {(areasData.some(area => area.availableHours < 0) || areasData.some(area => area.status === 'critical' || area.status === 'high')) && (
          <Card 
            variant="glass" 
            className={`p-3 ${
              areasData.some(area => area.availableHours < 0) 
                ? 'border-red-500/30 bg-red-500/10' 
                : 'border-orange-500/30 bg-orange-500/10'
            }`}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle 
                className={`w-4 h-4 mt-0.5 ${
                  areasData.some(area => area.availableHours < 0) ? 'text-red-400' : 'text-orange-400'
                }`}
              />
              <div className={`text-xs ${
                areasData.some(area => area.availableHours < 0) ? 'text-red-300' : 'text-orange-300'
              }`}>
                <div className="font-medium mb-1">Atenção!</div>
                {areasData.filter(area => area.availableHours < 0).length > 0 && (
                  <div className="mb-1">Áreas com horas excedidas: {
                    areasData
                      .filter(area => area.availableHours < 0)
                      .map(area => `${TaskAreaLabels[area.area as keyof typeof TaskAreaLabels] || area.area} (${Math.abs(area.availableHours)}h excedido)`)
                      .join(', ')
                  }</div>
                )}
                {areasData.filter(area => area.status === 'critical' && area.availableHours >= 0).length > 0 && (
                  <div>Áreas com utilização crítica (&gt;90%): {
                    areasData
                      .filter(area => area.status === 'critical' && area.availableHours >= 0)
                      .map(area => TaskAreaLabels[area.area as keyof typeof TaskAreaLabels] || area.area)
                      .join(', ')
                  }</div>
                )}
                {areasData.filter(area => area.status === 'high' && area.availableHours >= 0).length > 0 && (
                  <div>Áreas com alta utilização (&gt;75%): {
                    areasData
                      .filter(area => area.status === 'high' && area.availableHours >= 0)
                      .map(area => TaskAreaLabels[area.area as keyof typeof TaskAreaLabels] || area.area)
                      .join(', ')
                  }</div>
                )}
              </div>
            </div>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};